import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface IconChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** The icon to center inside the chip. */
  children: ReactNode;
  /** Diameter of the round chip in pixels. Defaults to 44. */
  size?: number;
  /** Active state: lime tint background with green ink. Defaults to neutral. */
  active?: boolean;
}

/**
 * Round icon pill (pastille d'icone). Centers an icon in a circle.
 * Neutral: bg-tile + text-ink-600. Active: lime tint + green ink.
 * Themed via design tokens; distinguishes apps by their icon, never colour.
 */
export const IconChip = forwardRef<HTMLSpanElement, IconChipProps>(function IconChip(
  { children, size = 44, active = false, className, style, ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        active ? 'bg-accent-soft text-accent-soft-ink' : 'bg-tile text-ink-600',
        className,
      )}
      // size en px de reference (1920) : rendu en rem pour suivre l'echelle globale.
      style={{ width: `${size / 16}rem`, height: `${size / 16}rem`, ...style }}
      {...props}
    >
      {children}
    </span>
  );
});
